/**
 * Aba "Solicitações" do Painel de Assistências — regras puras: catálogo de
 * colunas, filtro estilo Excel por coluna e o formato da visualização salva.
 *
 * Contrato do filtro (o mesmo do componente FiltroExcel): `null` = coluna sem
 * filtro; `Set` = seleção explícita, e Set vazio não deixa nada passar.
 */
import { ESTADO_PRAZO_LABEL, SITUACAO_LABELS } from "../../../services/solicitacoes";
import { formatarDataLocal } from "../../../utils/dinheiro";

export const VAZIO = "(vazio)";
export const CHAVE_PREFERENCIA = "painel_assistencias_solicitacoes";

// Listas que o painel sabe montar (`conjunto` do endpoint /itens). A primeira
// é o recorte do topo da página; as demais são filas de "agora".
export const CONJUNTOS = [
  { value: "recorte", label: "Emitidas no período" },
  { value: "abertas", label: "Abertas agora" },
  { value: "aguardando_autorizacao", label: "Aguardando autorização" },
  { value: "vencidas", label: "Vencidas" },
  { value: "prontas_sem_retirada", label: "Prontas sem retirada" },
  { value: "pecas_sem_lote", label: "Peças novas sem lote" },
  { value: "sem_classificacao", label: "Classificação pendente" },
  { value: "sem_prazo", label: "Sem prazo" },
];

const dataHora = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");

/**
 * `valor(l)` é o texto exibido E o valor do filtro. `filtro: false` = coluna
 * de valor quase único por linha (número, datas): filtrar por lista não ajuda.
 */
export const COLUNAS = [
  { key: "numero", label: "Nº", largura: 124, filtro: false, fixa: true, valor: (l) => l.numero },
  { key: "tipo", label: "Tipo", largura: 60, valor: (l) => l.tipo },
  { key: "situacao", label: "Situação", largura: 160, valor: (l) => SITUACAO_LABELS[l.situacao] || l.situacao_label || l.situacao },
  { key: "cliente", label: "Cliente", valor: (l) => l.cliente },
  { key: "num_pedido", label: "Pedido", largura: 90, valor: (l) => l.num_pedido },
  { key: "solicitante", label: "Solicitante", valor: (l) => l.solicitante },
  { key: "itens", label: "Itens", largura: 60, num: true, filtro: false, tipoExport: "number", valor: (l) => l.itens },
  { key: "created_at", label: "Emissão", largura: 92, filtro: false, tipoExport: "date", valor: (l) => dataHora(l.created_at), bruto: (l) => l.created_at },
  { key: "idade_du", label: "Idade (d.u.)", largura: 92, num: true, filtro: false, tipoExport: "number", valor: (l) => (l.idade_du ?? "") },
  { key: "prazo", label: "Prazo vigente", largura: 104, filtro: false, tipoExport: "date", valor: (l) => formatarDataLocal(l.prazo_entrega_vigente_em), bruto: (l) => l.prazo_entrega_vigente_em },
  { key: "estado_prazo", label: "Situação do prazo", largura: 150, valor: (l) => ESTADO_PRAZO_LABEL[l.estado_prazo] || "" },
  { key: "rota", label: "Rota", largura: 130, valor: (l) => l.prazo_rota_label },
  { key: "replanejado", label: "Replanejada", largura: 100, valor: (l) => (l.replanejado ? "Sim" : "Não") },
  { key: "autorizado_por", label: "Autorizado por", valor: (l) => l.autorizado_por },
  { key: "autorizada_em", label: "Autorizada em", largura: 104, filtro: false, tipoExport: "date", valor: (l) => dataHora(l.autorizada_em), bruto: (l) => l.autorizada_em },
  { key: "pronta_em", label: "Pronta em", largura: 96, filtro: false, tipoExport: "date", valor: (l) => dataHora(l.pronta_em), bruto: (l) => l.pronta_em },
  { key: "expedida_em", label: "Retirada em", largura: 96, filtro: false, tipoExport: "date", valor: (l) => dataHora(l.expedida_em), bruto: (l) => l.expedida_em },
  { key: "atendida_em", label: "Atendida em", largura: 96, filtro: false, tipoExport: "date", valor: (l) => dataHora(l.atendida_em), bruto: (l) => l.atendida_em },
  { key: "lider", label: "Líder de montagem", valor: (l) => l.lider },
  { key: "supervisor", label: "Supervisor", valor: (l) => l.supervisor },
  { key: "ambientes", label: "Ambientes", valor: (l) => l.ambientes },
  { key: "materiais", label: "Materiais", valor: (l) => l.materiais },
  { key: "ncs", label: "Não conformidade", valor: (l) => l.ncs },
  { key: "processos", label: "Processo de origem", valor: (l) => l.processos },
  { key: "causas", label: "Causa-raiz", valor: (l) => l.causas },
  { key: "imputacoes", label: "Imputação", largura: 120, valor: (l) => l.imputacoes },
  { key: "lotes", label: "Lotes", largura: 100, valor: (l) => l.lotes },
  { key: "motivo_encerramento", label: "Motivo da recusa/cancelamento", filtro: false, valor: (l) => l.motivo_encerramento },
];

export const COLUNAS_PADRAO = [
  "numero", "tipo", "situacao", "cliente", "solicitante", "itens", "created_at", "prazo", "estado_prazo",
];

const POR_KEY = new Map(COLUNAS.map((c) => [c.key, c]));
export const colunaDe = (key) => POR_KEY.get(key);

/** Mantém só chaves conhecidas, na ordem do catálogo, sempre com as fixas. */
export function normalizarColunas(keys) {
  const pedido = new Set(Array.isArray(keys) && keys.length ? keys : COLUNAS_PADRAO);
  return COLUNAS.filter((c) => c.fixa || pedido.has(c.key)).map((c) => c.key);
}

export function valorFiltro(coluna, linha) {
  const v = coluna.valor(linha);
  return v === null || v === undefined || v === "" ? VAZIO : String(v);
}

/** A linha passa em todos os filtros, exceto o da coluna `ignorar`. */
export function passaNosFiltros(linha, filtros, ignorar = "") {
  return Object.entries(filtros || {}).every(([key, set]) => {
    if (!set || key === ignorar) return true;
    const coluna = POR_KEY.get(key);
    return coluna ? set.has(valorFiltro(coluna, linha)) : true;
  });
}

/** Opções de uma coluna com a contagem sob os DEMAIS filtros (padrão Excel). */
export function opcoesDaColuna(coluna, linhas, filtros) {
  const contagem = new Map();
  (linhas || []).forEach((l) => {
    if (!passaNosFiltros(l, filtros, coluna.key)) return;
    const v = valorFiltro(coluna, l);
    contagem.set(v, (contagem.get(v) || 0) + 1);
  });
  // Valor já selecionado continua na lista mesmo zerado: dá para desmarcar.
  (filtros?.[coluna.key] || []).forEach((v) => { if (!contagem.has(v)) contagem.set(v, 0); });
  return [...contagem.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], "pt-BR", { numeric: true }))
    .map(([value, count]) => ({ value, label: value, count }));
}

// ---- ordenação (clique no nome da coluna: A→Z, Z→A, sem ordem) ----

export function proximaOrdenacao(atual, key) {
  if (atual?.key !== key) return { key, dir: "asc" };
  return atual.dir === "asc" ? { key, dir: "desc" } : null;
}

function chaveDeOrdem(coluna, linha) {
  if (coluna.bruto) return coluna.bruto(linha) || "";
  const v = coluna.valor(linha);
  if (coluna.num) return v === "" || v === null || v === undefined ? null : Number(v);
  return v === null || v === undefined ? "" : String(v);
}

/** Vazios sempre no fim, nos dois sentidos (como o Excel). */
export function ordenarLinhas(linhas, ordenacao) {
  const coluna = ordenacao && POR_KEY.get(ordenacao.key);
  if (!coluna) return linhas;
  const sinal = ordenacao.dir === "desc" ? -1 : 1;
  const vazio = (v) => v === "" || v === null;
  return [...linhas].sort((a, b) => {
    const x = chaveDeOrdem(coluna, a);
    const y = chaveDeOrdem(coluna, b);
    if (vazio(x) || vazio(y)) return vazio(x) === vazio(y) ? 0 : (vazio(x) ? 1 : -1);
    if (typeof x === "number" && typeof y === "number") return sinal * (x - y);
    return sinal * String(x).localeCompare(String(y), "pt-BR", { numeric: true, sensitivity: "base" });
  });
}

// ---- visualização salva (preferência do usuário, cross-device) ----

export function paraPreferencia({ colunas, filtros, ordenacao }) {
  const f = {};
  Object.entries(filtros || {}).forEach(([k, set]) => { if (set) f[k] = [...set].sort(); });
  return {
    v: 1, colunas: normalizarColunas(colunas), filtros: f,
    ordenacao: ordenacao && POR_KEY.has(ordenacao.key) ? { key: ordenacao.key, dir: ordenacao.dir } : null,
  };
}

export function dePreferencia(pref) {
  const filtros = {};
  Object.entries(pref?.filtros || {}).forEach(([k, lista]) => {
    if (POR_KEY.has(k) && Array.isArray(lista)) filtros[k] = new Set(lista);
  });
  const o = pref?.ordenacao;
  const ordenacao = o && POR_KEY.has(o.key) ? { key: o.key, dir: o.dir === "desc" ? "desc" : "asc" } : null;
  return { colunas: normalizarColunas(pref?.colunas), filtros, ordenacao };
}

export const assinatura = (estado) => JSON.stringify(paraPreferencia(estado));
